import React, { useEffect, useRef, useState } from 'react';

// A controlled numeric input that actually accepts decimals.
//
// A plain number-bound <input value={someNumber} onChange={e => set(Number(e.target.value))}>
// CANNOT hold a trailing dot: typing "1500." fires onChange, Number("1500.") === 1500, the box is
// re-rendered as "1500", and the dot is eaten — so the user can never reach "1500.50". The fix is to
// keep the raw text locally while the user edits, and report a parsed number upward. The local text
// is re-synced only when the numeric value changes from the OUTSIDE (e.g. a service price is picked,
// or a receipt amount snaps to the document net) — never on the user's own keystrokes.
export default function NumInput({
  value, onChange, style, placeholder,
}: {
  value: number;
  onChange: (n: number) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}) {
  const [text, setText] = useState<string>(value ? String(value) : '');
  const lastNum = useRef<number>(value);

  useEffect(() => {
    if (value !== lastNum.current) {
      setText(value ? String(value) : '');
      lastNum.current = value;
    }
  }, [value]);

  return (
    <input
      value={text}
      inputMode="decimal"
      placeholder={placeholder}
      style={style}
      onChange={e => {
        const v = e.target.value;
        if (v !== '' && !/^\d*\.?\d*$/.test(v)) return; // digits with at most one decimal point
        setText(v);
        const n = v === '' || v === '.' ? 0 : Number(v);
        const num = Number.isFinite(n) ? n : 0;
        lastNum.current = num;
        onChange(num);
      }}
    />
  );
}
